import { useEffect, useMemo, useState } from "react";

import { SubscriptionIntervals, type SubscriptionPlans } from "@prisma/client";
import clsx from "clsx";
import { formatDistanceToNowStrict } from "date-fns";

import Icon from "@musicfy/components/Icon";
import { SubscriptionPlanInfo } from "@musicfy/contants/Subscriptions";
import { api } from "@musicfy/utils/api";

const ROTATE_INTERVAL_MS = 4000;
const RELATIVE_TIME_TICK_MS = 30_000;

const planLabel = (plan: SubscriptionPlans): string => {
  const info = (
    SubscriptionPlanInfo as Record<string, { label: string } | undefined>
  )[plan];
  if (info) return info.label;
  return plan.charAt(0).toUpperCase() + plan.slice(1);
};

const intervalSuffix = (interval: SubscriptionIntervals): string =>
  interval === SubscriptionIntervals.year ? "yearly" : "monthly";

const RecentUpgradesTicker: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  const { data: upgrades } = api.subscription.getRecentUpgrades.useQuery(
    undefined,
    {
      staleTime: 60_000,
      refetchInterval: 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const [activeIndex, setActiveIndex] = useState(0);
  // re-render every 30s so the "8 minutes ago" string stays fresh
  const [, setNowTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setNowTick((t) => t + 1);
    }, RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const items = useMemo(() => upgrades ?? [], [upgrades]);

  useEffect(() => {
    if (items.length <= 1) {
      setActiveIndex(0);
      return;
    }
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % items.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) {
    return null;
  }

  const active = items[activeIndex] ?? items[0];
  if (!active) return null;

  const relativeTime = formatDistanceToNowStrict(new Date(active.createdAt), {
    addSuffix: true,
  });

  return (
    <div
      className={clsx("mx-auto w-full max-w-md flex justify-center", className)}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="relative flex items-center gap-3 rounded-full border border-pink-200/40 bg-gray-1000/60 backdrop-blur-md px-4 py-2 shadow-lg">
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-300 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-pink-300" />
        </span>

        <div
          key={active.key}
          className="flex items-center gap-2 text-14 text-white/90 animate-fade-in"
        >
          <Icon name="crown" className="w-3.5 h-auto opacity-80" />
          <span className="truncate">
            <span className="font-semibold text-white">
              {active.displayName}
            </span>{" "}
            upgraded to{" "}
            <span className="font-semibold text-pink-200">
              {planLabel(active.plan)}
            </span>{" "}
            <span className="text-white/60">
              ({intervalSuffix(active.interval)}) · {relativeTime}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default RecentUpgradesTicker;
